import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '../context/AppContext';

const API_URL = 'https://songmoment.onrender.com';

function formatTime(ms) {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function timeAgo(date) {
  const diff = (Date.now() - new Date(date)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function StatCard({ label, value, emoji }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: '0.5px solid rgba(212,83,126,0.15)',
      borderRadius: 12, padding: '16px', flex: 1, minWidth: 0,
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 20, marginBottom: 6 }}>{emoji}</div>
      <div style={{
        fontSize: 22, fontWeight: 800, color: '#D4537E',
        fontFamily: "'Cormorant Garamond', serif",
      }}>{value}</div>
      <div style={{ fontSize: 11, color: 'rgba(245,230,237,0.35)', marginTop: 4, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
    </div>
  );
}

export default function ProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, token } = useApp();
  const [profile, setProfile] = useState(null);
  const [moments, setMoments] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('moments');
  const [editing, setEditing] = useState(false);
  const [editBio, setEditBio] = useState('');
  const [editFavSong, setEditFavSong] = useState('');
  const [editFavArtist, setEditFavArtist] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isOwnProfile = user?.username === username;

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const userRes = await axios.get(`${API_URL}/api/users/username/${username}`);
      setProfile(userRes.data);
      setEditBio(userRes.data.bio || '');
      setEditFavSong(userRes.data.fav_song || '');
      setEditFavArtist(userRes.data.fav_song_artist || '');

      const [momentsRes, collectionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/moments/user/${userRes.data.id}`),
        axios.get(`${API_URL}/api/collections/user/${userRes.data.id}`),
      ]);
      setMoments(momentsRes.data);
      setCollections(collectionsRes.data.filter(c => c.is_public || isOwnProfile));
    } catch (err) {
      setError('User not found');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/users/${user.id}`, {
        bio: editBio,
        fav_song: editFavSong,
        fav_song_artist: editFavArtist,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setProfile(prev => ({ ...prev, ...res.data }));
      setEditing(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const totalReactions = moments.reduce((sum, m) =>
    sum + (m.reactions || []).reduce((s, r) => s + parseInt(r.count || 0), 0), 0);
  const totalComments = moments.reduce((sum, m) => sum + (m.comment_count || 0), 0);

  const allTags = [...new Set(moments.flatMap(m => m.tags || []))];

  const avatarColor = `hsl(${profile?.username?.charCodeAt(0) * 15 || 200}, 60%, 35%)`;

  const tabStyle = (tab) => ({
    fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase',
    padding: '8px 20px', borderRadius: 20, cursor: 'pointer',
    background: activeTab === tab ? 'rgba(212,83,126,0.15)' : 'transparent',
    border: `0.5px solid ${activeTab === tab ? '#D4537E' : 'rgba(212,83,126,0.2)'}`,
    color: activeTab === tab ? '#D4537E' : 'rgba(245,230,237,0.45)',
    transition: 'all 0.15s',
  });

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#080608', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 16, opacity: 0.25 }}>♪</div>
        <p style={{ fontSize: 11, color: 'rgba(245,230,237,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Loading profile...</p>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#080608', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 36, marginBottom: 16, opacity: 0.25 }}>😕</div>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, color: 'rgba(245,230,237,0.65)', marginBottom: 16 }}>User not found</p>
        <button onClick={() => navigate('/')} style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#D4537E', background: 'none', border: '0.5px solid rgba(212,83,126,0.4)', borderRadius: 20, padding: '8px 20px', cursor: 'pointer' }}>
          ← Back to feed
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#080608' }}>
      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(8,6,8,0.92)', backdropFilter: 'blur(20px)',
        borderBottom: '0.5px solid rgba(212,83,126,0.2)', padding: '0 24px',
      }}>
        <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 62 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => navigate('/')}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg, #D4537E, #993556)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#fff' }}>♪</div>
            <span style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 20, letterSpacing: '0.15em', color: '#f5e6ed', textTransform: 'uppercase' }}>Echo Moments</span>
          </div>
          <button onClick={() => navigate('/')} style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(245,230,237,0.45)', background: 'none', border: '0.5px solid rgba(212,83,126,0.3)', borderRadius: 20, padding: '6px 16px', cursor: 'pointer' }}>
            ← Feed
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 680, margin: '0 auto', padding: '40px 24px 60px' }}>

        {/* Profile header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 32 }}>
          {/* Avatar */}
          <div style={{
            width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
            background: avatarColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 28, fontWeight: 700, color: '#fff',
            border: '2px solid rgba(212,83,126,0.3)',
          }}>
            {profile?.username?.[0]?.toUpperCase()}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
              <h1 style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 300, fontSize: 28,
                letterSpacing: '0.05em', color: '#f5e6ed',
              }}>@{profile?.username}</h1>
              {isOwnProfile && !editing && (
                <button onClick={() => setEditing(true)} style={{
                  fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase',
                  color: 'rgba(212,83,126,0.7)', background: 'none',
                  border: '0.5px solid rgba(212,83,126,0.3)',
                  borderRadius: 20, padding: '4px 12px', cursor: 'pointer',
                }}>Edit profile</button>
              )}
            </div>

            {/* Bio */}
            {!editing ? (
              <>
                {profile?.bio && (
                  <p style={{ fontSize: 13, color: 'rgba(245,230,237,0.6)', marginBottom: 8, lineHeight: 1.5 }}>
                    {profile.bio}
                  </p>
                )}
                {profile?.fav_song && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    background: 'rgba(212,83,126,0.08)',
                    border: '0.5px solid rgba(212,83,126,0.2)',
                    borderRadius: 20, padding: '5px 12px',
                  }}>
                    <span style={{ fontSize: 12 }}>🎵</span>
                    <span style={{ fontSize: 12, color: 'rgba(245,230,237,0.7)' }}>
                      {profile.fav_song}
                      {profile.fav_song_artist && <span style={{ color: 'rgba(245,230,237,0.4)' }}> · {profile.fav_song_artist}</span>}
                    </span>
                  </div>
                )}
                {!profile?.bio && !profile?.fav_song && isOwnProfile && (
                  <p style={{ fontSize: 12, color: 'rgba(245,230,237,0.25)', fontStyle: 'italic' }}>
                    Add a bio and your favourite song...
                  </p>
                )}
              </>
            ) : (
              /* Edit form */
              <div style={{ marginTop: 8 }}>
                <textarea
                  value={editBio}
                  onChange={e => setEditBio(e.target.value)}
                  placeholder="Write a bio..."
                  rows={2}
                  style={{
                    width: '100%', background: 'rgba(255,255,255,0.04)',
                    border: '0.5px solid rgba(212,83,126,0.3)',
                    borderRadius: 8, padding: '8px 12px',
                    fontSize: 13, color: '#f5e6ed', resize: 'none',
                    marginBottom: 8, boxSizing: 'border-box',
                  }}
                />
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    value={editFavSong}
                    onChange={e => setEditFavSong(e.target.value)}
                    placeholder="Favourite song..."
                    style={{
                      flex: 1, background: 'rgba(255,255,255,0.04)',
                      border: '0.5px solid rgba(212,83,126,0.3)',
                      borderRadius: 8, padding: '8px 12px',
                      fontSize: 13, color: '#f5e6ed',
                    }}
                  />
                  <input
                    value={editFavArtist}
                    onChange={e => setEditFavArtist(e.target.value)}
                    placeholder="Artist..."
                    style={{
                      flex: 1, background: 'rgba(255,255,255,0.04)',
                      border: '0.5px solid rgba(212,83,126,0.3)',
                      borderRadius: 8, padding: '8px 12px',
                      fontSize: 13, color: '#f5e6ed',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={saveProfile} disabled={saving} style={{
                    fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: '#fff', background: '#D4537E',
                    border: 'none', borderRadius: 20, padding: '7px 18px', cursor: 'pointer',
                    opacity: saving ? 0.6 : 1,
                  }}>{saving ? 'Saving...' : 'Save'}</button>
                  <button onClick={() => setEditing(false)} style={{
                    fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
                    color: 'rgba(245,230,237,0.45)', background: 'none',
                    border: '0.5px solid rgba(255,255,255,0.1)',
                    borderRadius: 20, padding: '7px 18px', cursor: 'pointer',
                  }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 32 }}>
          <StatCard label="Moments" value={moments.length} emoji="🎵" />
          <StatCard label="Reactions" value={totalReactions} emoji="🔥" />
          <StatCard label="Comments" value={totalComments} emoji="💬" />
        </div>

        {/* Tags/Vibes */}
        {allTags.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            <p style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(212,83,126,0.6)', marginBottom: 10 }}>
              Vibes
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {allTags.map(tag => (
                <span key={tag} style={{
                  fontSize: 11, padding: '4px 12px', borderRadius: 20,
                  background: 'rgba(212,83,126,0.08)',
                  border: '0.5px solid rgba(212,83,126,0.25)',
                  color: '#ED93B1', letterSpacing: '0.03em',
                }}>{tag}</span>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          <button onClick={() => setActiveTab('moments')} style={tabStyle('moments')}>
            Moments ({moments.length})
          </button>
          <button onClick={() => setActiveTab('collections')} style={tabStyle('collections')}>
            Collections ({collections.length})
          </button>
        </div>

        {/* Moments tab */}
        {activeTab === 'moments' && (
          <div>
            {moments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.2 }}>🎵</div>
                <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: 'rgba(245,230,237,0.5)' }}>No moments yet</p>
              </div>
            ) : (
              moments.map(m => (
                <div key={m.id} style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '0.5px solid rgba(212,83,126,0.12)',
                  borderRadius: 14, padding: 14, marginBottom: 10,
                  display: 'flex', alignItems: 'center', gap: 12,
                  transition: 'border-color 0.2s',
                }}
                  onMouseOver={e => e.currentTarget.style.borderColor = 'rgba(212,83,126,0.3)'}
                  onMouseOut={e => e.currentTarget.style.borderColor = 'rgba(212,83,126,0.12)'}
                >
                  {m.album_image
                    ? <img src={m.album_image} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                    : <div style={{ width: 48, height: 48, borderRadius: 8, background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>♪</div>
                  }
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400, fontSize: 16, color: '#f5e6ed', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.track_name}</div>
                    <div style={{ fontSize: 12, color: 'rgba(245,230,237,0.4)', marginTop: 2 }}>{m.artist_name}</div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 5, fontSize: 11, color: 'rgba(245,230,237,0.3)' }}>
                      <span>⏱ {formatTime(m.timestamp_ms)}</span>
                      <span>🔥 {(m.reactions || []).reduce((s, r) => s + parseInt(r.count || 0), 0)}</span>
                      <span>💬 {m.comment_count || 0}</span>
                      <span>{timeAgo(m.created_at)}</span>
                    </div>
                    {m.tags?.length > 0 && (
                      <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                        {m.tags.map(tag => (
                          <span key={tag} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: 'rgba(212,83,126,0.08)', border: '0.5px solid rgba(212,83,126,0.2)', color: '#ED93B1' }}>{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Collections tab */}
        {activeTab === 'collections' && (
          <div>
            {collections.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.2 }}>🎵</div>
                <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: 'rgba(245,230,237,0.5)' }}>No collections yet</p>
              </div>
            ) : (
              collections.map(c => (
                <div key={c.id} style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '0.5px solid rgba(212,83,126,0.12)',
                  borderRadius: 14, padding: 16, marginBottom: 10,
                  display: 'flex', alignItems: 'center', gap: 12,
                  cursor: 'pointer', transition: 'border-color 0.2s',
                }}
                  onClick={() => navigate('/collections')}
                  onMouseOver={e => e.currentTarget.style.borderColor = 'rgba(212,83,126,0.3)'}
                  onMouseOut={e => e.currentTarget.style.borderColor = 'rgba(212,83,126,0.12)'}
                >
                  <div style={{ width: 48, height: 48, borderRadius: 10, background: `hsl(${c.name.charCodeAt(0) * 20}, 40%, 20%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🎵</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, color: '#f5e6ed', marginBottom: 2 }}>{c.name}</div>
                    {c.description && <div style={{ fontSize: 12, color: 'rgba(245,230,237,0.4)', marginBottom: 4 }}>{c.description}</div>}
                    <div style={{ fontSize: 11, color: 'rgba(245,230,237,0.3)' }}>{c.moment_count} moments · {!c.is_public ? '🔒 Private' : '🌐 Public'}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}